import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Mail,
  FlaskConical,
  BarChart3,
  ToggleLeft,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react';
import { signOut } from '@shared/services/auth';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/admin-dashboard', icon: <LayoutDashboard size={20} /> },
  { label: 'User Management', path: '/admin-dashboard/users', icon: <Users size={20} /> },
  { label: 'Waitlist', path: '/admin-dashboard/waitlist', icon: <Mail size={20} /> },
  { label: 'Content Lab', path: '/admin-dashboard/content-lab', icon: <FlaskConical size={20} /> },
  { label: 'Analytics', path: '/admin-dashboard/analytics', icon: <BarChart3 size={20} /> },
  { label: 'Feature Flags', path: '/admin-dashboard/feature-flags', icon: <ToggleLeft size={20} /> },
  { label: 'Settings', path: '/admin-dashboard/settings', icon: <Settings size={20} /> },
];

export function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate('/admin-login');
  }

  const sidebarWidth = collapsed ? 64 : 230;

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-50 md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-dark-surface border border-dark-border text-content"
      >
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-screen flex flex-col border-r border-dark-border transition-all duration-300 ease-in-out md:sticky md:top-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        style={{
          width: mobileOpen ? 230 : sidebarWidth,
          background: 'linear-gradient(180deg, #0E1225, #080B17)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-dark-border shrink-0">
          {!collapsed && (
            <span className="font-heading text-[15px] font-bold text-white tracking-tight">
              Cogni<span className="text-indigo-400">Print</span>{' '}
              <span className="text-[10px] font-normal text-content-muted uppercase tracking-wider">Admin</span>
            </span>
          )}
          {collapsed && (
            <span className="font-heading text-lg font-bold text-indigo-400 mx-auto">C</span>
          )}

          {/* Mobile close */}
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden text-content-muted hover:text-content"
          >
            <X size={18} />
          </button>
        </div>

        {/* Collapse toggle — desktop only */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex absolute items-center justify-center w-6 h-6 rounded-full bg-dark-surface border border-dark-border text-content-muted hover:text-content hover:scale-110 transition-all"
          style={{ right: -12, top: 80, zIndex: 60 }}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <ul className="space-y-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/admin-dashboard'}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                      collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'
                    } ${
                      isActive
                        ? 'bg-indigo-500/15 text-indigo-400 border-l-[3px] border-indigo-400'
                        : 'text-content-muted hover:text-content hover:bg-white/5 border-l-[3px] border-transparent'
                    }`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <span className="shrink-0">{item.icon}</span>
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom actions */}
        <div className="shrink-0 border-t border-dark-border p-3 space-y-2">
          <button
            onClick={() => {
              navigate('/dashboard');
              setMobileOpen(false);
            }}
            className={`w-full flex items-center gap-3 rounded-lg text-[13px] font-medium text-gold-400 hover:bg-gold-400/10 transition-colors ${
              collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'
            }`}
            title={collapsed ? 'View as User' : undefined}
          >
            <Users size={18} />
            {!collapsed && <span>View as User</span>}
          </button>
          <button
            onClick={handleSignOut}
            className={`w-full flex items-center gap-3 rounded-lg text-[13px] font-medium text-red-400 hover:bg-red-400/10 transition-colors ${
              collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'
            }`}
            title={collapsed ? 'Sign Out' : undefined}
          >
            <LogOut size={18} />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
